/**
 * wd-predictive-search.js
 * Standalone predictive search for the WD header sections.
 *
 * The Empire theme's LiveSearch class only initialises for its own
 * static-header section. Our custom wd-header / wd-hero-header sections
 * are invisible to that system, so we replicate the core fetch-and-display
 * logic here, using the same data-* attributes and DOM structure.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 300;

  // All live-search containers that live inside a WD header
  var containers = document.querySelectorAll(
    '.wd-header [data-live-search], .wd-hero-header__overlay [data-live-search]'
  );

  if (!containers.length) return;

  containers.forEach(function (container) {
    if (container.dataset.wdSearchInit === 'true') return;
    container.dataset.wdSearchInit = 'true';
    initSearch(container);
  });

  function initSearch(container) {
    var input       = container.querySelector('[data-live-search-input]');
    var flydown     = container.querySelector('[data-live-search-flydown]');
    var resultsEl   = container.querySelector('[data-live-search-results]');
    var placeholder = container.querySelector('[data-live-search-placeholder]');
    var quickLinks  = container.querySelector('[data-live-search-quick-links]');
    var clearBtn    = container.querySelector('[data-live-search-clear]');

    if (!input || !flydown) return;

    var timer     = null;
    var lastQuery = '';

    // ---- Clear button visibility -------------------------------------------
    function updateClearButton() {
      if (!clearBtn) return;
      clearBtn.style.display = input.value.length > 0 ? 'inline-flex' : 'none';
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        input.value = '';
        lastQuery = '';
        updateClearButton();
        if (quickLinks) {
          showOnly('quicklinks');
          openFlydown();
        } else {
          closeFlydown();
        }
        input.focus();
      });
    }

    // ---- Flydown visibility --------------------------------------------------
    var heroHeader = container.closest('.wd-hero-header');

    function positionFlydown() {
      // Position the flydown directly below the form. Compute the form's own
      // height (offsetHeight) — independent of viewport position — and apply
      // it with !important to beat Empire's legacy LiveSearch JS.
      // Width/left/right are left to the CSS rule.
      if (window.matchMedia('(min-width: 750px)').matches) {
        var formEl = container.querySelector('[data-live-search-form]') ||
                     flydown.parentElement;
        var topPx = (formEl.offsetHeight || 52) + 6;
        flydown.style.setProperty('top', topPx + 'px', 'important');
      } else {
        flydown.style.removeProperty('top');
      }
    }

    function openFlydown() {
      positionFlydown();
      flydown.classList.add('wd-flydown-open');
      container.classList.add('wd-search-active');
      if (heroHeader) heroHeader.classList.add('wd-search-flydown-open');
      setTimeout(function () {
        document.addEventListener('click', onDocClick, true);
        document.addEventListener('focusin', onDocFocus, true);
      }, 10);
      window.addEventListener('keydown', onEsc);
    }

    function closeFlydown() {
      flydown.classList.remove('wd-flydown-open');
      container.classList.remove('wd-search-active');
      if (heroHeader) heroHeader.classList.remove('wd-search-flydown-open');
      document.removeEventListener('click', onDocClick, true);
      document.removeEventListener('focusin', onDocFocus, true);
      window.removeEventListener('keydown', onEsc);
    }

    function onDocClick(e) {
      if (!container.contains(e.target)) closeFlydown();
    }

    function onDocFocus(e) {
      if (!container.contains(e.target)) closeFlydown();
    }

    function onEsc(e) {
      if (e.key === 'Escape') {
        closeFlydown();
        input.blur();
      }
    }

    // ---- Section visibility helpers -----------------------------------------
    function showOnly(section) {
      if (placeholder) placeholder.classList.toggle('visible', section === 'placeholder');
      if (resultsEl)   resultsEl.classList.toggle('visible',   section === 'results');
      if (quickLinks)  quickLinks.classList.toggle('visible',  section === 'quicklinks');
    }

    // ---- Search -------------------------------------------------------------
    function doSearch() {
      var terms = input.value.trim();
      updateClearButton();

      if (!terms) {
        lastQuery = '';
        if (quickLinks) {
          showOnly('quicklinks');
          openFlydown();
        } else {
          closeFlydown();
        }
        return;
      }

      // Skip re-fetching the same query
      if (terms === lastQuery && resultsEl && resultsEl.children.length > 0) {
        showOnly('results');
        openFlydown();
        return;
      }

      lastQuery = terms;
      showOnly('placeholder');
      openFlydown();

      var baseUrl = '/search/suggest';
      if (window.Theme && window.Theme.routes && window.Theme.routes.predictive_search_url) {
        baseUrl = window.Theme.routes.predictive_search_url;
      }

      var url = baseUrl
        + '?q=' + encodeURIComponent(terms)
        + '&section_id=predictive-search'
        + '&resources[type]=product,query,collection,page'
        + '&resources[limit]=16';

      fetch(url)
        .then(function (res) {
          if (!res.ok) throw new Error(res.status);
          return res.text();
        })
        .then(function (html) {
          if (resultsEl) resultsEl.innerHTML = html;
          showOnly('results');
          openFlydown();
        })
        .catch(function (err) {
          console.warn('[wd-search] Fetch error:', err);
        });
    }

    // ---- Events -------------------------------------------------------------
    input.addEventListener('input', function () {
      clearTimeout(timer);
      updateClearButton();
      timer = setTimeout(doSearch, DEBOUNCE_MS);
    });

    input.addEventListener('focus', function () {
      // On narrow screens the mobile overlay takes over — stop flydown from opening
      if (window.matchMedia('(max-width: 719px)').matches) {
        if (typeof window.openMobileSearch === 'function') {
          window.openMobileSearch();
          return;
        }
      }

      var terms = input.value.trim();
      if (terms) {
        if (resultsEl && resultsEl.children.length > 0) {
          showOnly('results');
          openFlydown();
        } else {
          doSearch();
        }
      } else if (quickLinks) {
        showOnly('quicklinks');
        openFlydown();
      }
    });

    console.log('[wd-search] Predictive search ready');
  }
})();
